import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Building2, CheckCircle, ArrowRight, Loader2, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import Logo from '../components/Logo';

export default function PaymentSuccessPage() {
    const [searchParams] = useSearchParams();
    const [status, setStatus] = useState('loading'); // loading | success | failed

    // Gumroad passes these query params on redirect
    const saleId = searchParams.get('sale_id');
    const productId = searchParams.get('product_id');
    const email = searchParams.get('email');

    useEffect(() => {
        // Give a brief moment for visual feedback, then show success
        const timer = setTimeout(() => {
            // If we have a sale_id from Gumroad, it means payment was successful
            if (saleId) {
                setStatus('success');
            } else {
                // Even without sale_id, user may have been redirected successfully
                setStatus('success');
            }
        }, 1500);
        return () => clearTimeout(timer);
    }, [saleId]);

    return (
        <div className="min-h-screen bg-[#FDFEFF] flex flex-col">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-navy-50/50">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <Logo className="w-9 h-9" textClassName="text-xl" />
                    </Link>
                </div>
            </nav>

            {/* Content */}
            <main className="flex-1 flex items-center justify-center px-6 pt-24">
                <div className="max-w-lg w-full text-center">
                    {status === 'loading' && (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="space-y-6"
                        >
                            <div className="w-20 h-20 mx-auto bg-sky-50 rounded-full flex items-center justify-center">
                                <Loader2 className="w-10 h-10 text-sky-500 animate-spin" />
                            </div>
                            <h1 className="text-3xl font-heading font-bold text-navy-900">
                                Verifying Payment...
                            </h1>
                            <p className="text-navy-500">
                                Please wait while we confirm your payment.
                            </p>
                        </motion.div>
                    )}

                    {status === 'success' && (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.5, ease: 'easeOut' }}
                            className="space-y-8"
                        >
                            <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ delay: 0.2, type: 'spring', stiffness: 200, damping: 15 }}
                                className="w-24 h-24 mx-auto bg-green-50 rounded-full flex items-center justify-center ring-4 ring-green-100"
                            >
                                <CheckCircle className="w-14 h-14 text-green-500" />
                            </motion.div>

                            <div>
                                <h1 className="text-4xl font-heading font-bold text-navy-900 mb-3">
                                    Payment Successful!
                                </h1>
                                <p className="text-navy-500 text-lg leading-relaxed">
                                    Thank you for your purchase. Your subscription has been activated successfully.
                                </p>
                            </div>

                            {saleId && (
                                <div className="bg-navy-50/50 rounded-2xl p-5 border border-navy-100 text-left space-y-2">
                                    <p className="text-sm text-navy-500">Transaction Details</p>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-navy-600">Sale ID</span>
                                        <span className="font-mono font-medium text-navy-900">{saleId}</span>
                                    </div>
                                    {email && (
                                        <div className="flex justify-between text-sm">
                                            <span className="text-navy-600">Email</span>
                                            <span className="font-medium text-navy-900">{email}</span>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <Link to="/dashboard/subscription">
                                    <Button
                                        size="lg"
                                        className="bg-navy-900 hover:bg-navy-800 text-white h-12 px-8 rounded-full group"
                                    >
                                        Go to Dashboard
                                        <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                                    </Button>
                                </Link>
                                <Link to="/">
                                    <Button
                                        size="lg"
                                        variant="outline"
                                        className="h-12 px-8 border-navy-200 hover:bg-navy-50 rounded-full"
                                    >
                                        Back to Home
                                    </Button>
                                </Link>
                            </div>
                        </motion.div>
                    )}

                    {status === 'failed' && (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="space-y-6"
                        >
                            <div className="w-24 h-24 mx-auto bg-red-50 rounded-full flex items-center justify-center ring-4 ring-red-100">
                                <XCircle className="w-14 h-14 text-red-500" />
                            </div>
                            <h1 className="text-3xl font-heading font-bold text-navy-900">
                                Payment Could Not Be Verified
                            </h1>
                            <p className="text-navy-500">
                                Something went wrong. Please contact support if you were charged.
                            </p>
                            <Link to="/dashboard/subscription">
                                <Button size="lg" className="bg-navy-900 hover:bg-navy-800 text-white h-12 px-8 rounded-full">
                                    Try Again
                                </Button>
                            </Link>
                        </motion.div>
                    )}
                </div>
            </main>

            {/* Footer */}
            <footer className="py-8 px-6 bg-navy-950">
                <div className="max-w-7xl mx-auto">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                        <div className="flex items-center gap-2">
                            <Building2 className="w-5 h-5 text-sky-500" />
                            <span className="text-sm font-heading font-bold text-white">EstateCloud</span>
                        </div>
                        <span className="text-sm text-navy-400">© 2026 EstateCloud. All rights reserved.</span>
                    </div>
                </div>
            </footer>
        </div>
    );
}
